/**
 * Presentational shell for the Showcase page. It reads the DTO from
 * `getElizaShowcase` and formats it for the screen only — no counts, no
 * business logic here (those live in the use case). Split out from the mount in
 * index.tsx so tests can render the component without a DOM container.
 */

import { getElizaShowcase } from "./showcase.js";

const styles = {
  main: {
    fontFamily: "system-ui, sans-serif",
    maxWidth: "48rem",
    margin: "0 auto",
    padding: "2.5rem 1.5rem 4rem",
    color: "#1a1a1a",
    lineHeight: 1.55,
  },
  tagline: { fontSize: "1.15rem", color: "#444", marginTop: "0.25rem" },
  statStrip: {
    display: "flex",
    gap: "2rem",
    flexWrap: "wrap" as const,
    margin: "1.75rem 0",
    padding: "1rem 1.25rem",
    background: "#f5f3ff",
    borderRadius: "0.75rem",
  },
  statValue: { fontSize: "1.6rem", fontWeight: 700, color: "#6d28d9" },
  statLabel: { fontSize: "0.8rem", color: "#555", textTransform: "uppercase" as const },
  sectionTitle: { fontSize: "1.25rem", marginTop: "2.25rem", marginBottom: "0.75rem" },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(14rem, 1fr))",
    gap: "0.9rem",
  },
  card: {
    border: "1px solid #e5e5e5",
    borderRadius: "0.6rem",
    padding: "0.9rem 1rem",
    background: "#fff",
  },
  cardTitle: { margin: "0 0 0.35rem", fontSize: "1rem" },
  cardDetail: { margin: 0, fontSize: "0.9rem", color: "#555" },
  link: { color: "#6d28d9", fontWeight: 600 },
} as const;

export function App() {
  const showcase = getElizaShowcase();

  return (
    <main style={styles.main}>
      <h1 style={{ margin: 0 }}>{showcase.name}</h1>
      <p style={styles.tagline}>{showcase.tagline}</p>
      <p>{showcase.description}</p>

      <div style={styles.statStrip}>
        {showcase.stats.map((stat) => (
          <div key={stat.label}>
            <div style={styles.statValue}>{stat.value}</div>
            <div style={styles.statLabel}>{stat.label}</div>
          </div>
        ))}
      </div>

      <h2 style={styles.sectionTitle}>What makes it different</h2>
      <div style={styles.cardGrid}>
        {showcase.features.map((feature) => (
          <div key={feature.title} style={styles.card}>
            <h3 style={styles.cardTitle}>{feature.title}</h3>
            <p style={styles.cardDetail}>{feature.detail}</p>
          </div>
        ))}
      </div>

      <h2 style={styles.sectionTitle}>Runtime primitives a plugin registers</h2>
      <div style={styles.cardGrid}>
        {showcase.primitives.map((primitive) => (
          <div key={primitive.name} style={styles.card}>
            <h3 style={styles.cardTitle}>{primitive.name}</h3>
            <p style={styles.cardDetail}>{primitive.summary}</p>
          </div>
        ))}
      </div>

      <p style={{ marginTop: "2.5rem" }}>
        <a style={styles.link} href={showcase.repositoryUrl}>
          View elizaOS on GitHub →
        </a>
      </p>
    </main>
  );
}

export default App;
